import debounce from 'lodash/debounce';
import React, {
  useRef, useState, useCallback, useMemo,
} from 'react';
import useEventListener from './useEventListener';

export default function useSearchInMenu({
  searchIcon,
  searchInMenu,
  searchInputRender,
  searchPlaceholder = 'Search...',
  options: inputOptions,
  children: inputChildren,
  filterOption,
  optionFilterProp,
  dropdownRender,
  onSearch,
  ...inputProps
}) {
  const ref = useRef({});
  const [search, setSearch] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const searchEnabled = searchInMenu && filterOption !== false;
  const onSearchInputKeydown = useCallback((e) => {
    e.stopPropagation();
  }, []);
  const onSearchInputChange = useCallback((e) => {
    setSearchInputValue(e.target.value);
    ref.current.search(e.target.value);
  }, []);
  const filterOptionDefault = useCallback((inputValue, option) => {
    const prop = option[ref.current.optionFilterProp];
    return typeof prop === 'string' && prop.includes(inputValue);
  }, []);

  ref.current.filterOption = typeof filterOption === 'function' ? filterOption : filterOptionDefault;
  ref.current.optionFilterProp = optionFilterProp || 'value';

  // 启用 searchInMenu 时，代理实现搜索功能，筛选 options 和 children
  const options = useMemo(
    () => (Array.isArray(inputOptions) && searchEnabled
      ? inputOptions.filter((option) => ref.current.filterOption(search, option))
      : inputOptions
    ),
    [inputOptions, searchEnabled, search],
  );
  const children = searchEnabled
    ? React.Children.map(
      inputChildren,
      (child) => ref.current.filterOption(search, child.props) && child,
    )
    : inputChildren;

  // 菜单隐藏时清空搜索词
  const onDropdownVisibleChange = useEventListener(inputProps, 'onDropdownVisibleChange', (visible) => {
    if (!visible) {
      setSearchInputValue('');
      setSearch('');
    }
  });

  const dropdownRenderWrapper = (menu) => (
    <>
      <div className={`${inputProps.prefixCls}-search`}>
        <input
          type="text"
          className="ge-input"
          placeholder={searchPlaceholder}
          value={searchInputValue}
          onKeyDown={onSearchInputKeydown}
          onChange={onSearchInputChange}
        />
        {searchIcon}
      </div>
      {typeof dropdownRender === 'function' ? dropdownRender(menu) : menu}
    </>
  );

  ref.current.onSearch = typeof onSearch === 'function' ? onSearch : () => null;
  if (!ref.current.search) {
    ref.current.search = debounce((value) => {
      ref.current.onSearch(value);
      setSearch(value);
    }, 500);
  }

  const props = {
    filterOption,
    optionFilterProp,
    dropdownRender,
    children,
    options,
    ...inputProps,
  };

  if (searchInMenu) {
    props.dropdownRender = dropdownRenderWrapper;
    props.onDropdownVisibleChange = onDropdownVisibleChange;
    props.showSearch = false;
  } else {
    props.showSearch = props.showSearch !== undefined ? props.showSearch : props.mode === 'multiple';
    props.onSearch = props.showSearch ? onSearch : undefined;
  }
  return props;
}
